import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  EMPTY_CUSTOMER_FORM,
  LAYOUT_COMBINED_NOTE,
  formatStageBspPercentLabel,
  PLC_OPTIONS,
  PLC_HALF_RATE_NOTE,
  SAMPLE_CUSTOMERS,
  buildCustomerRecord,
  buildManualPricingSeed,
  calculateBsp,
  calculatePlcAmount,
  buildPaymentPlan,
  reconcileManualStages,
  resolvePaymentPlan,
  customerToForm,
  formatPlcLabels,
  formatPlotNumber,
  formatTentativeSize,
  sumPaymentPlan,
} from "./adminUtils";
import GoogleSignInButton from "./components/GoogleSignInButton";
import BrandHeader from "./components/BrandHeader";
import { SKIP_ADMIN_LOGIN, UI_PREVIEW_MODE, USE_FIREBASE_AUTH, isAdminEmail } from "./config";
import {
  generateAccessToken,
  loadPreviewCustomers,
  savePreviewCustomers,
} from "./previewStore";
import { useAuth } from "./useAuth";
import { apiRequest, formatCurrency, formatDate } from "./utils";

const EMPTY_PAYMENT_DRAFT = { amount: "", date: "", chequeRtgs: "", bank: "" };
const PLC_STAGE_IDS = new Set(["layout", "rera", "electricity"]);

function paymentEditKey(customerId, index) {
  return `${customerId}-${index}`;
}

function toDateInputValue(date) {
  if (!date) return "";
  return String(date).slice(0, 10);
}

function applyPaymentTotals(customer, payments) {
  const paidAmount = payments.reduce((sum, payment) => sum + (Number(payment.amount) || 0), 0);
  return {
    ...customer,
    payments,
    paidAmount,
    pendingAmount: customer.totalAmount - paidAmount,
    lastUpdatedAt: new Date().toISOString(),
  };
}

export default function AdminPanel() {
  const { user, idToken, loading: authLoading, signInWithGoogle, signOut } = useAuth();
  const [customers, setCustomers] = useState(() =>
    UI_PREVIEW_MODE ? loadPreviewCustomers(SAMPLE_CUSTOMERS) : []
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [form, setForm] = useState(EMPTY_CUSTOMER_FORM);
  const [paymentDrafts, setPaymentDrafts] = useState({});
  const [editingPayment, setEditingPayment] = useState(null);
  const [paymentEditDrafts, setPaymentEditDrafts] = useState({});
  const [expandedId, setExpandedId] = useState(null);
  const [view, setView] = useState("register");
  const [searchQuery, setSearchQuery] = useState("");
  const [editingCustomerId, setEditingCustomerId] = useState(null);

  const authToken = USE_FIREBASE_AUTH ? idToken : null;

  const isAdminUser = useMemo(() => {
    if (SKIP_ADMIN_LOGIN) return true;
    if (!USE_FIREBASE_AUTH || !user?.email) return false;
    if (UI_PREVIEW_MODE) return isAdminEmail(user.email);
    return true;
  }, [user]);

  const isLoggedIn = SKIP_ADMIN_LOGIN || (USE_FIREBASE_AUTH ? Boolean(user && isAdminUser) : false);

  const calculatedBsp = calculateBsp(form.sizeSqYds, form.basicRatePerSqYd);
  const calculatedPlc = calculatePlcAmount(
    calculatedBsp,
    form.plc,
    form.noPlc,
    form.plcPending,
    form.customPlc
  );
  const previewBsp = form.manualPricing ? Number(form.manualBsp) || 0 : calculatedBsp;
  const previewPlc = form.manualPricing
    ? (form.plcPending ? 0 : Number(form.manualPlcAmount) || 0)
    : calculatedPlc;
  const previewTotal = previewBsp + previewPlc;
  const previewPlan = resolvePaymentPlan(form, calculatedBsp, calculatedPlc);

  const displayStages = useMemo(() => {
    const rows = [];

    for (const stage of previewPlan) {
      if (form.layoutCombined && stage.id === "layout") {
        const plotManual = form.manualStages.plotAllocation;
        rows.push({
          id: "plotAllocation",
          label: "On Plot Allocation",
          bspPercent: previewBsp > 0 ? 0.125 : 0,
          bspAmount: plotManual?.bspAmount
            ? Number(plotManual.bspAmount) || 0
            : Math.round(previewBsp * 0.125),
          plcPercent: 0,
          plcAmount: plotManual?.plcAmount ? Number(plotManual.plcAmount) || 0 : 0,
          totalAmount: plotManual?.bspAmount
            ? (Number(plotManual.bspAmount) || 0) + (Number(plotManual.plcAmount) || 0)
            : Math.round(previewBsp * 0.125),
          disabled: true,
          isToken: false,
        });
      }

      rows.push({ ...stage, disabled: false });

      if (stage.id === "layout") {
        rows.push({ id: "layoutNote", isNote: true });
      }
    }

    return rows;
  }, [previewPlan, form.layoutCombined, form.manualPricing, form.manualStages, previewBsp]);

  const fetchCustomers = useCallback(async (tokenOverride = authToken) => {
    setLoading(true);
    setError("");
    try {
      const data = await apiRequest("/api/admin/customers", { method: "GET" }, tokenOverride);
      setCustomers(data);
    } catch (err) {
      if (err.message.includes("not authorized")) {
        await signOut();
      }
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authToken, signOut]);

  useEffect(() => {
    if (isLoggedIn && !UI_PREVIEW_MODE && authToken) fetchCustomers();
  }, [isLoggedIn, authToken, fetchCustomers]);

  useEffect(() => {
    if (!UI_PREVIEW_MODE) return;
    const loaded = loadPreviewCustomers(SAMPLE_CUSTOMERS);
    let changed = false;
    const migrated = loaded.map((c) => {
      if (c.accessToken) return c;
      changed = true;
      return { ...c, accessToken: c.customerId === "SOE-1023" ? "demo" : generateAccessToken() };
    });
    if (changed) savePreviewCustomers(migrated);
    setCustomers(migrated);
  }, []);

  const sortedCustomers = useMemo(
    () => [...customers].sort((a, b) => new Date(b.lastUpdatedAt).getTime() - new Date(a.lastUpdatedAt).getTime()),
    [customers]
  );

  const filteredCustomers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return sortedCustomers;
    return sortedCustomers.filter((c) =>
      [c.name, c.userId, c.email, c.mobile, c.customerId, c.plotNumber, c.aadhaar, c.pan, c.address, c.applicant2?.name, c.applicant2?.mobile]
        .some((field) => String(field || "").toLowerCase().includes(q))
    );
  }, [sortedCustomers, searchQuery]);

  const stats = useMemo(() => ({
    total: customers.length,
    collected: customers.reduce((sum, c) => sum + (c.paidAmount || 0), 0),
    pending: customers.reduce((sum, c) => sum + (c.pendingAmount || 0), 0),
  }), [customers]);

  const persistPreviewCustomers = (nextCustomers) => {
    setCustomers(nextCustomers);
    if (UI_PREVIEW_MODE) savePreviewCustomers(nextCustomers);
  };

  const updateForm = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const toggleManualPricing = (enabled) => {
    setForm((prev) => {
      if (!enabled) {
        return { ...prev, manualPricing: false };
      }

      const bsp = calculateBsp(prev.sizeSqYds, prev.basicRatePerSqYd);
      const plc = calculatePlcAmount(
        bsp,
        prev.plc,
        prev.noPlc,
        prev.plcPending,
        prev.customPlc
      );
      const plan = buildPaymentPlan(bsp, plc, prev.tokenAmount, prev.layoutCombined);
      const seed = buildManualPricingSeed(bsp, plc, plan);

      return {
        ...prev,
        manualPricing: true,
        ...seed,
      };
    });
  };

  const updateManualStage = (stageId, field, value) => {
    setForm((prev) => {
      const manualStages = {
        ...prev.manualStages,
        [stageId]: {
          ...(prev.manualStages[stageId] || { bspAmount: "", plcAmount: "" }),
          [field]: value,
        },
      };

      if (stageId === "token" && field === "bspAmount") {
        return { ...prev, manualStages, tokenAmount: value };
      }

      return { ...prev, manualStages };
    });
  };

  const updateLayoutCombined = (checked) => {
    setForm((prev) => {
      const next = { ...prev, layoutCombined: checked };
      if (!prev.manualPricing) return next;

      const bsp = Number(prev.manualBsp) || calculateBsp(prev.sizeSqYds, prev.basicRatePerSqYd);
      const plc = prev.plcPending
        ? 0
        : Number(prev.manualPlcAmount) || calculatePlcAmount(
            bsp,
            prev.plc,
            prev.noPlc,
            prev.plcPending,
            prev.customPlc
          );
      const fallbackPlan = buildPaymentPlan(bsp, plc, prev.tokenAmount, checked);

      return {
        ...next,
        manualStages: reconcileManualStages(prev.manualStages, checked, fallbackPlan),
      };
    });
  };

  const setNoPlc = () => {
    setForm((prev) => ({
      ...prev,
      noPlc: true,
      plcPending: false,
      customPlc: { ...prev.customPlc, enabled: false },
      plc: { corner: false, green: false, wideRoad: false },
    }));
  };

  const setPlcPending = () => {
    setForm((prev) => ({
      ...prev,
      noPlc: false,
      plcPending: true,
      customPlc: { ...prev.customPlc, enabled: false },
      plc: { corner: false, green: false, wideRoad: false },
    }));
  };

  const togglePlc = (id) => {
    setForm((prev) => ({
      ...prev,
      noPlc: false,
      plcPending: false,
      plc: { ...prev.plc, [id]: !prev.plc[id] },
    }));
  };

  const resetForm = () => {
    setForm(EMPTY_CUSTOMER_FORM);
    setEditingCustomerId(null);
  };

  const startEdit = (customer) => {
    setForm(customerToForm(customer));
    setEditingCustomerId(customer.customerId);
    setView("register");
    setError("");
    setSuccess("");
  };

  const handleDeleteCustomer = async (customerId) => {
    if (!window.confirm("Delete this customer application? This cannot be undone.")) return;

    setLoading(true);
    setError("");
    setSuccess("");

    if (UI_PREVIEW_MODE) {
      persistPreviewCustomers(customers.filter((c) => c.customerId !== customerId));
      if (expandedId === customerId) setExpandedId(null);
      if (editingCustomerId === customerId) resetForm();
      setSuccess("Customer deleted.");
      setLoading(false);
      return;
    }

    try {
      await apiRequest(`/api/admin/customers/${customerId}`, { method: "DELETE" }, authToken);
      if (expandedId === customerId) setExpandedId(null);
      if (editingCustomerId === customerId) resetForm();
      setSuccess("Customer deleted.");
      await fetchCustomers();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError("");
    try {
      const signedInUser = await signInWithGoogle();
      if (UI_PREVIEW_MODE) {
        const email = signedInUser.email?.toLowerCase() || "";
        if (!isAdminEmail(email)) {
          await signOut();
          setError("Your Google account is not authorized for admin access.");
          return;
        }
        return;
      }
      const token = await signedInUser.getIdToken();
      await fetchCustomers(token);
    } catch (err) {
      setError(err.message || "Google sign-in failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitCustomer = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    const existing = editingCustomerId
      ? customers.find((c) => c.customerId === editingCustomerId)
      : null;
    const record = buildCustomerRecord(form, existing || { accessToken: generateAccessToken() });

    if (UI_PREVIEW_MODE) {
      const next = editingCustomerId
        ? customers.map((c) => (c.customerId === editingCustomerId ? record : c))
        : [record, ...customers];
      persistPreviewCustomers(next);
      setExpandedId(record.customerId);
      resetForm();
      setSuccess(`Application ${record.customerId} ${editingCustomerId ? "updated" : "registered"} successfully.`);
      setView("customers");
      setLoading(false);
      return;
    }

    try {
      if (editingCustomerId) {
        await apiRequest(`/api/admin/customers/${editingCustomerId}`, { method: "PUT", body: JSON.stringify(record) }, authToken);
      } else {
        await apiRequest("/api/admin/customers", { method: "POST", body: JSON.stringify(record) }, authToken);
      }
      resetForm();
      setSuccess(`Application ${record.customerId} ${editingCustomerId ? "updated" : "registered"} successfully.`);
      setView("customers");
      await fetchCustomers();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const addPayment = async (customerId) => {
    const draft = paymentDrafts[customerId] || EMPTY_PAYMENT_DRAFT;
    if (!draft.amount || !draft.date) return setError("Enter payment amount and date");

    setLoading(true);
    setError("");
    setSuccess("");

    const payment = {
      amount: Number(draft.amount),
      date: draft.date,
      chequeRtgs: draft.chequeRtgs || "",
      bank: draft.bank || "",
    };

    if (UI_PREVIEW_MODE) {
      const next = customers.map((customer) => {
        if (customer.customerId !== customerId) return customer;
        if (payment.amount > customer.pendingAmount) {
          setError("Amount exceeds pending balance");
          return customer;
        }
        const paidAmount = customer.paidAmount + payment.amount;
        return {
          ...customer,
          payments: [...customer.payments, payment],
          paidAmount,
          pendingAmount: customer.totalAmount - paidAmount,
          lastUpdatedAt: new Date().toISOString(),
        };
      });
      persistPreviewCustomers(next);
      setPaymentDrafts((prev) => ({ ...prev, [customerId]: EMPTY_PAYMENT_DRAFT }));
      setSuccess("Payment recorded.");
      setLoading(false);
      return;
    }

    try {
      await apiRequest(`/api/admin/customers/${customerId}/payments`, { method: "POST", body: JSON.stringify(payment) }, authToken);
      setPaymentDrafts((prev) => ({ ...prev, [customerId]: EMPTY_PAYMENT_DRAFT }));
      setSuccess("Payment recorded.");
      await fetchCustomers();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const startEditPayment = (customerId, index, payment) => {
    setEditingPayment({ customerId, index });
    setPaymentEditDrafts((prev) => ({
      ...prev,
      [paymentEditKey(customerId, index)]: {
        amount: payment.amount != null ? String(payment.amount) : "",
        date: toDateInputValue(payment.date),
        chequeRtgs: payment.chequeRtgs || "",
        bank: payment.bank || "",
      },
    }));
    setError("");
    setSuccess("");
  };

  const cancelEditPayment = () => {
    setEditingPayment(null);
  };

  const updatePaymentEditDraft = (customerId, index, field, value) => {
    const key = paymentEditKey(customerId, index);
    setPaymentEditDrafts((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || EMPTY_PAYMENT_DRAFT), [field]: value },
    }));
  };

  const savePaymentEdit = async (customerId, index) => {
    const draft = paymentEditDrafts[paymentEditKey(customerId, index)] || EMPTY_PAYMENT_DRAFT;
    if (!draft.amount || !draft.date) return setError("Enter payment amount and date");

    setLoading(true);
    setError("");
    setSuccess("");

    const payment = {
      amount: Number(draft.amount),
      date: draft.date,
      chequeRtgs: draft.chequeRtgs || "",
      bank: draft.bank || "",
    };

    if (UI_PREVIEW_MODE) {
      const customer = customers.find((c) => c.customerId === customerId);
      if (!customer) {
        setLoading(false);
        return;
      }

      const payments = [...(customer.payments || [])];
      payments[index] = payment;
      const paidAmount = payments.reduce((sum, item) => sum + item.amount, 0);
      if (paidAmount > customer.totalAmount) {
        setError("Total paid amount exceeds customer balance");
        setLoading(false);
        return;
      }

      const next = customers.map((item) => (
        item.customerId === customerId ? applyPaymentTotals(item, payments) : item
      ));
      persistPreviewCustomers(next);
      setEditingPayment(null);
      setSuccess("Payment updated.");
      setLoading(false);
      return;
    }

    try {
      await apiRequest(
        `/api/admin/customers/${customerId}/payments/${index}`,
        { method: "PUT", body: JSON.stringify(payment) },
        authToken
      );
      setEditingPayment(null);
      setSuccess("Payment updated.");
      await fetchCustomers();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="admin-page">
        <div className="admin-login-card">
          <p>Loading…</p>
        </div>
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <div className="admin-page">
        <div className="admin-login-card">
          <p className="admin-eyebrow">Staff Access</p>
          <h2>Admin Login</h2>
          <p className="portal-login-note">Sign in with an authorized Google account.</p>
          {USE_FIREBASE_AUTH ? (
            <GoogleSignInButton onClick={handleGoogleLogin} disabled={loading} />
          ) : (
            <p className="admin-error">Firebase is not configured. Add REACT_APP_FIREBASE_* keys to .env, or enable preview bypass.</p>
          )}
          {user && !isAdminUser && (
            <button type="button" className="admin-btn admin-btn--ghost" style={{ marginTop: "1rem", width: "100%" }} onClick={signOut}>
              Sign out ({user.email})
            </button>
          )}
          {error && <p className="admin-error">{error}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <header className="admin-topbar">
        <div className="admin-topbar__brand">
          <BrandHeader logoSize="sm" subtitle="Admin Portal" />
        </div>
        <div className="admin-topbar__actions">
          {user?.email && <span className="admin-topbar__user">{user.email}</span>}
          {!SKIP_ADMIN_LOGIN && (
            <button className="admin-btn admin-btn--danger" type="button" onClick={signOut}>
              Logout
            </button>
          )}
        </div>
      </header>

      <div className="admin-stats">
        <div className="admin-stat">
          <span>Total Customers</span>
          <strong>{stats.total}</strong>
        </div>
        <div className="admin-stat admin-stat--accent">
          <span>Collected</span>
          <strong>{formatCurrency(stats.collected)}</strong>
        </div>
        <div className="admin-stat">
          <span>Pending</span>
          <strong>{formatCurrency(stats.pending)}</strong>
        </div>
      </div>

      {(error || success) && (
        <div className={`admin-alert ${error ? "admin-alert--error" : "admin-alert--success"}`}>
          {error || success}
        </div>
      )}

      <div className="admin-tabs">
        <button
          type="button"
          className={`admin-tab ${view === "register" ? "admin-tab--active" : ""}`}
          onClick={() => { setView("register"); if (editingCustomerId) resetForm(); }}
        >
          {editingCustomerId ? "Edit Application" : "New Application"}
        </button>
        <button type="button" className={`admin-tab ${view === "customers" ? "admin-tab--active" : ""}`} onClick={() => setView("customers")}>
          All Customers ({customers.length})
        </button>
      </div>

      {view === "register" && (
        <form onSubmit={handleSubmitCustomer} className="admin-register">
          <section className="admin-panel">
            <div className="admin-panel__head">
              <h3>Applicant Details</h3>
              <p>Personal information as per application form</p>
            </div>
            <div className="admin-grid admin-grid--2">
              <label className="admin-field">
                <span>Name</span>
                <input value={form.name} onChange={(e) => updateForm("name", e.target.value)} placeholder="Full name" />
              </label>
              <label className="admin-field">
                <span>User ID</span>
                <input value={form.userId} onChange={(e) => updateForm("userId", e.target.value)} placeholder="e.g. USR-1001" />
              </label>
              <label className="admin-field">
                <span>Email ID</span>
                <input value={form.email} onChange={(e) => updateForm("email", e.target.value)} type="email" placeholder="customer@gmail.com (optional)" />
              </label>
              <label className="admin-field">
                <span>Contact No.</span>
                <input
                  value={form.mobile}
                  onChange={(e) => updateForm("mobile", e.target.value.replace(/\D/g, "").slice(0, 10))}
                  placeholder="10-digit mobile"
                  inputMode="numeric"
                  maxLength={10}
                />
              </label>
              <label className="admin-field admin-field--full">
                <span>Address</span>
                <textarea value={form.address} onChange={(e) => updateForm("address", e.target.value)} placeholder="Complete postal address" rows={2} />
              </label>
              <label className="admin-field">
                <span>Aadhaar No.</span>
                <input value={form.aadhaar} onChange={(e) => updateForm("aadhaar", e.target.value)} placeholder="XXXX-XXXX-XXXX" />
              </label>
              <label className="admin-field">
                <span>PAN No.</span>
                <input value={form.pan} onChange={(e) => updateForm("pan", e.target.value)} placeholder="ABCDE1234F" />
              </label>
              <label className="admin-field admin-field--full admin-plc-pending">
                <span className="admin-checkbox-label">
                  <input
                    type="checkbox"
                    checked={form.jointDeal}
                    onChange={(e) => setForm((prev) => ({ ...prev, jointDeal: e.target.checked }))}
                  />
                  Joint deal — add second applicant
                </span>
              </label>
            </div>
          </section>

          {form.jointDeal && (
            <section className="admin-panel">
              <div className="admin-panel__head">
                <h3>Applicant 2 Details</h3>
                <p>Second applicant for joint deal</p>
              </div>
              <div className="admin-grid admin-grid--2">
                <label className="admin-field">
                  <span>Name</span>
                  <input
                    value={form.applicant2.name}
                    onChange={(e) => setForm((prev) => ({ ...prev, applicant2: { ...prev.applicant2, name: e.target.value } }))}
                    placeholder="Full name"
                  />
                </label>
                <label className="admin-field">
                  <span>Email ID</span>
                  <input
                    value={form.applicant2.email}
                    onChange={(e) => setForm((prev) => ({ ...prev, applicant2: { ...prev.applicant2, email: e.target.value } }))}
                    type="email"
                    placeholder="customer@gmail.com"
                  />
                </label>
                <label className="admin-field">
                  <span>Contact No.</span>
                  <input
                    value={form.applicant2.mobile}
                    onChange={(e) => setForm((prev) => ({
                      ...prev,
                      applicant2: { ...prev.applicant2, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) },
                    }))}
                    placeholder="10-digit mobile"
                    inputMode="numeric"
                    maxLength={10}
                  />
                </label>
                <label className="admin-field admin-field--full">
                  <span>Address</span>
                  <textarea
                    value={form.applicant2.address}
                    onChange={(e) => setForm((prev) => ({ ...prev, applicant2: { ...prev.applicant2, address: e.target.value } }))}
                    placeholder="Complete postal address"
                    rows={2}
                  />
                </label>
                <label className="admin-field">
                  <span>Aadhaar No.</span>
                  <input
                    value={form.applicant2.aadhaar}
                    onChange={(e) => setForm((prev) => ({ ...prev, applicant2: { ...prev.applicant2, aadhaar: e.target.value } }))}
                    placeholder="XXXX-XXXX-XXXX"
                  />
                </label>
                <label className="admin-field">
                  <span>PAN No.</span>
                  <input
                    value={form.applicant2.pan}
                    onChange={(e) => setForm((prev) => ({ ...prev, applicant2: { ...prev.applicant2, pan: e.target.value } }))}
                    placeholder="ABCDE1234F"
                  />
                </label>
              </div>
            </section>
          )}

          <section className="admin-panel">
            <div className="admin-panel__head">
              <h3>Tentative Property Details</h3>
              <p>Upcoming proposed project — plot allotment details</p>
            </div>
            <label className="admin-field admin-field--full">
              <span className="admin-checkbox-label">
                <input
                  type="checkbox"
                  checked={form.manualPricing}
                  onChange={(e) => toggleManualPricing(e.target.checked)}
                />
                Enter BSP, PLC and payment plan amounts manually
              </span>
            </label>
            <div className="admin-grid admin-grid--2 admin-grid--spaced-top">
              <label className="admin-field">
                <span>Number of Residential Plots</span>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={form.inventoryCount}
                  onChange={(e) => updateForm("inventoryCount", e.target.value)}
                  placeholder="1"
                />
              </label>
              <label className="admin-field">
                <span>Category</span>
                <input value={form.category} readOnly />
              </label>
              <label className="admin-field">
                <span>Plot Number</span>
                <input
                  value={form.plotNumber}
                  onChange={(e) => updateForm("plotNumber", e.target.value)}
                  placeholder="e.g. P-21"
                  disabled={form.plotPending}
                />
              </label>
              <label className="admin-field admin-field--full admin-plc-pending">
                <span className="admin-checkbox-label">
                  <input
                    type="checkbox"
                    checked={form.plotPending}
                    onChange={(e) => setForm((prev) => ({
                      ...prev,
                      plotPending: e.target.checked,
                      plotNumber: e.target.checked ? "" : prev.plotNumber,
                    }))}
                  />
                  Plot number will be declared after plot allocation on layout launch
                </span>
              </label>
              <label className="admin-field">
                <span>Tentative Size (Sq. Yds)</span>
                <input type="number" value={form.sizeSqYds} onChange={(e) => updateForm("sizeSqYds", e.target.value)} placeholder="200" />
              </label>
              <label className="admin-field">
                <span>Size Remarks (optional)</span>
                <input
                  type="text"
                  value={form.sizeRemarks}
                  onChange={(e) => updateForm("sizeRemarks", e.target.value)}
                />
              </label>
              <label className="admin-field">
                <span>Tentative Basic Rate (per Sq. Yd)</span>
                <input type="number" value={form.basicRatePerSqYd} onChange={(e) => updateForm("basicRatePerSqYd", e.target.value)} placeholder="12500" />
              </label>
              <div className="admin-field">
                <span>Basic Selling Price (BSP)</span>
                {form.manualPricing ? (
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={form.manualBsp}
                    onChange={(e) => updateForm("manualBsp", e.target.value)}
                    placeholder="Enter BSP amount"
                  />
                ) : (
                  <div className="admin-readonly">{formatCurrency(previewBsp)}</div>
                )}
              </div>
              {form.manualPricing && !form.manualBsp && calculatedBsp > 0 && (
                <div className="admin-field">
                  <span>Calculated reference</span>
                  <div className="admin-readonly admin-readonly--muted">{formatCurrency(calculatedBsp)} from size × rate</div>
                </div>
              )}
            </div>
          </section>

          <section className="admin-panel">
            <div className="admin-panel__head">
              <h3>Preferred Location Charges (PLC)</h3>
              <p>{PLC_HALF_RATE_NOTE}</p>
            </div>
            <div className={`admin-plc-grid${form.manualPricing ? " admin-plc-grid--disabled" : ""}`}>
              <label className={`admin-plc ${form.noPlc ? "admin-plc--active" : ""}`}>
                <input type="radio" name="plcChoice" checked={form.noPlc && !form.plcPending} onChange={setNoPlc} />
                <span className="admin-plc__label">No PLC</span>
                <span className="admin-plc__rate">0% — standard plot</span>
              </label>
              <label className={`admin-plc ${form.plcPending ? "admin-plc--active" : ""}`}>
                <input type="radio" name="plcChoice" checked={form.plcPending} onChange={setPlcPending} />
                <span className="admin-plc__label">PLC TBD</span>
                <span className="admin-plc__rate">Decided after final layout launch and plot allocation</span>
              </label>
              {PLC_OPTIONS.map((opt) => (
                <label key={opt.id} className={`admin-plc ${!form.noPlc && !form.plcPending && form.plc[opt.id] ? "admin-plc--active" : ""}`}>
                  <input
                    type="checkbox"
                    checked={!form.noPlc && !form.plcPending && form.plc[opt.id]}
                    onChange={() => togglePlc(opt.id)}
                    disabled={form.plcPending}
                  />
                  <span className="admin-plc__label">{opt.label}</span>
                  <span className="admin-plc__rate">{Math.round(opt.rate * 100)}% of BSP</span>
                </label>
              ))}
            </div>
            <div className="admin-custom-plc">
              <label className="admin-checkbox-label">
                <input
                  type="checkbox"
                  checked={form.customPlc.enabled}
                  onChange={(e) => setForm((prev) => ({
                    ...prev,
                    customPlc: { ...prev.customPlc, enabled: e.target.checked },
                    noPlc: e.target.checked ? false : prev.noPlc,
                    plcPending: e.target.checked ? false : prev.plcPending,
                  }))}
                  disabled={form.plcPending}
                />
                Add custom PLC (separate from standard options)
              </label>
              {form.customPlc.enabled && (
                <div className="admin-grid admin-grid--3 admin-custom-plc__fields">
                  <label className="admin-field">
                    <span>Type</span>
                    <select
                      value={form.customPlc.mode}
                      onChange={(e) => setForm((prev) => ({
                        ...prev,
                        customPlc: { ...prev.customPlc, mode: e.target.value },
                      }))}
                    >
                      <option value="percent">Percentage of BSP</option>
                      <option value="amount">Fixed amount (Rs.)</option>
                    </select>
                  </label>
                  {form.customPlc.mode === "percent" ? (
                    <label className="admin-field">
                      <span>PLC % of BSP</span>
                      <input
                        type="number"
                        min="0"
                        step="0.1"
                        value={form.customPlc.percent}
                        onChange={(e) => setForm((prev) => ({
                          ...prev,
                          customPlc: { ...prev.customPlc, percent: e.target.value },
                        }))}
                        placeholder="e.g. 5"
                      />
                    </label>
                  ) : (
                    <label className="admin-field">
                      <span>PLC amount (Rs.)</span>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={form.customPlc.amount}
                        onChange={(e) => setForm((prev) => ({
                          ...prev,
                          customPlc: { ...prev.customPlc, amount: e.target.value },
                        }))}
                        placeholder="e.g. 50000"
                      />
                    </label>
                  )}
                </div>
              )}
            </div>
            <div className="admin-summary-row">
              {form.manualPricing && !form.plcPending ? (
                <label className="admin-field">
                  <span>PLC Total (Rs.)</span>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={form.manualPlcAmount}
                    onChange={(e) => updateForm("manualPlcAmount", e.target.value)}
                    placeholder="Enter PLC total"
                  />
                </label>
              ) : (
                <div><span>PLC Total</span><strong>{form.plcPending ? "TBD" : formatCurrency(previewPlc)}</strong></div>
              )}
              <div><span>Grand Total (BSP + PLC)</span><strong>{form.plcPending ? `${formatCurrency(previewBsp)} + PLC TBD` : formatCurrency(previewTotal)}</strong></div>
            </div>
          </section>

          <section className="admin-panel">
            <div className="admin-panel__head">
              <h3>Payment Plan (If Allotted)</h3>
              {form.manualPricing && <p>Enter BSP and PLC amounts for each stage.</p>}
            </div>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Stage</th>
                    {!form.manualPricing && <th>BSP %</th>}
                    <th>Amount (Rs.)</th>
                    <th>PLC (Rs.)</th>
                    <th>Total (Rs.)</th>
                  </tr>
                </thead>
                <tbody>
                  {displayStages.map((stage) => {
                    if (stage.isNote) {
                      return (
                        <tr key="layout-note" className="admin-table__note-row">
                          <td colSpan={form.manualPricing ? 4 : 5}>
                            <div className="admin-payment-note">
                              <label className="admin-checkbox-label admin-payment-note__check">
                                <input
                                  type="checkbox"
                                  checked={form.layoutCombined}
                                  onChange={(e) => updateLayoutCombined(e.target.checked)}
                                />
                                <div>
                                  <p className="admin-payment-note__title">
                                    <strong>{LAYOUT_COMBINED_NOTE.title}</strong> {LAYOUT_COMBINED_NOTE.body}
                                  </p>
                                  <p className="admin-payment-note__footer">{LAYOUT_COMBINED_NOTE.footer}</p>
                                </div>
                              </label>
                            </div>
                          </td>
                        </tr>
                      );
                    }

                    const bspPercentLabel = formatStageBspPercentLabel(stage);
                    const manualStage = form.manualStages[stage.id] || { bspAmount: "", plcAmount: "" };
                    const showPlcTbd = form.plcPending && PLC_STAGE_IDS.has(stage.id);

                    return (
                    <tr key={stage.id} className={stage.disabled ? "admin-table__row--disabled" : ""}>
                      <td>{stage.label}</td>
                      {!form.manualPricing && (
                      <td>
                        {stage.isToken ? (
                          "—"
                        ) : stage.disabled ? (
                          <span className="admin-stage-disabled">{formatStageBspPercentLabel({ bspPercent: 0.125, plcPercent: 0 })} — N/A</span>
                        ) : (
                          bspPercentLabel
                        )}
                      </td>
                      )}
                      <td>
                        {stage.isToken ? (
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={form.manualPricing ? (manualStage.bspAmount || form.tokenAmount) : form.tokenAmount}
                            onChange={(e) => (
                              form.manualPricing
                                ? updateManualStage("token", "bspAmount", e.target.value)
                                : updateForm("tokenAmount", e.target.value)
                            )}
                            className="admin-table-input admin-table-input--wide"
                            placeholder={previewBsp ? String(Math.round(previewBsp * 0.25)) : "Amount"}
                            aria-label="Token booking amount in rupees"
                          />
                        ) : stage.disabled ? (
                          <span className="admin-stage-disabled">—</span>
                        ) : form.manualPricing ? (
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={manualStage.bspAmount}
                            onChange={(e) => updateManualStage(stage.id, "bspAmount", e.target.value)}
                            className="admin-table-input admin-table-input--wide"
                            placeholder="BSP amount"
                            aria-label={`${stage.label} BSP amount`}
                          />
                        ) : (
                          formatCurrency(stage.bspAmount)
                        )}
                      </td>
                      <td>
                        {stage.disabled ? (
                          <span className="admin-stage-disabled">—</span>
                        ) : showPlcTbd ? (
                          "TBD"
                        ) : form.manualPricing ? (
                          PLC_STAGE_IDS.has(stage.id) ? (
                            <input
                              type="number"
                              min="0"
                              step="1"
                              value={manualStage.plcAmount}
                              onChange={(e) => updateManualStage(stage.id, "plcAmount", e.target.value)}
                              className="admin-table-input admin-table-input--wide"
                              placeholder="PLC amount"
                              aria-label={`${stage.label} PLC amount`}
                            />
                          ) : (
                            "—"
                          )
                        ) : form.plcPending && stage.plcPercent > 0 ? (
                          "TBD"
                        ) : stage.plcAmount ? (
                          formatCurrency(stage.plcAmount)
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>
                        <strong>
                          {stage.disabled ? (
                            <span className="admin-stage-disabled">—</span>
                          ) : showPlcTbd ? (
                            `${formatCurrency(stage.bspAmount)} + PLC TBD`
                          ) : form.manualPricing ? (
                            formatCurrency(
                              (Number(manualStage.bspAmount) || 0) + (Number(manualStage.plcAmount) || 0)
                            )
                          ) : form.plcPending && stage.plcPercent > 0 ? (
                            `${formatCurrency(stage.bspAmount)} + PLC TBD`
                          ) : (
                            formatCurrency(stage.totalAmount)
                          )}
                        </strong>
                      </td>
                    </tr>
                    );
                  })}
                  <tr className="admin-table__total-row">
                    <td><strong>Total</strong></td>
                    {!form.manualPricing && <td><strong>—</strong></td>}
                    <td><strong>{formatCurrency(sumPaymentPlan("bspAmount", previewPlan))}</strong></td>
                    <td>
                      <strong>
                        {form.plcPending
                          ? "TBD"
                          : formatCurrency(sumPaymentPlan("plcAmount", previewPlan))}
                      </strong>
                    </td>
                    <td>
                      <strong>
                        {form.plcPending
                          ? `${formatCurrency(previewBsp)} + PLC TBD`
                          : formatCurrency(sumPaymentPlan("totalAmount", previewPlan))}
                      </strong>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <div className="admin-form-actions">
            {editingCustomerId && (
              <button className="admin-btn admin-btn--ghost" type="button" onClick={resetForm}>
                Cancel Edit
              </button>
            )}
            <button className="admin-btn admin-btn--primary" type="submit" disabled={loading}>
              {loading ? "Saving…" : editingCustomerId ? "Save Changes" : "Register Application"}
            </button>
          </div>
        </form>
      )}

      {view === "customers" && (
        <div className="admin-customers">
          <div className="admin-search">
            <input
              type="search"
              placeholder="Search by name, mobile, ID, plot, PAN, Aadhaar…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {loading && <p className="admin-muted">Loading…</p>}
          {!loading && filteredCustomers.length === 0 && (
            <p className="admin-muted">{searchQuery ? "No matching applications." : "No customers registered yet."}</p>
          )}

          {filteredCustomers.map((customer) => {
            const draft = paymentDrafts[customer.customerId] || EMPTY_PAYMENT_DRAFT;
            const isOpen = expandedId === customer.customerId;
            const paidPercent = customer.totalAmount ? Math.round((customer.paidAmount / customer.totalAmount) * 100) : 0;

            return (
              <article key={customer.customerId} className={`admin-customer ${isOpen ? "admin-customer--open" : ""}`}>
                <button type="button" className="admin-customer__head" onClick={() => setExpandedId(isOpen ? null : customer.customerId)}>
                  <div>
                    <p className="admin-customer__name">{customer.name}</p>
                    <p className="admin-customer__meta">
                      {customer.customerId} · Plot {formatPlotNumber(customer.plotNumber, customer.plotPending)} · {customer.email || customer.mobile}
                    </p>
                  </div>
                  <div className="admin-customer__amounts">
                    <span>{formatCurrency(customer.paidAmount)} paid</span>
                    <strong>{formatCurrency(customer.pendingAmount)} due</strong>
                    <div className="admin-customer__actions">
                      <Link
                        to={`/admin/receipt/${customer.customerId}`}
                        state={{ customer }}
                        className="admin-btn admin-btn--ghost admin-btn--sm"
                        onClick={(e) => e.stopPropagation()}
                      >
                        View Receipt
                      </Link>
                      <button
                        type="button"
                        className="admin-btn admin-btn--ghost admin-btn--sm"
                        onClick={(e) => { e.stopPropagation(); startEdit(customer); }}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="admin-btn admin-btn--danger admin-btn--sm"
                        onClick={(e) => { e.stopPropagation(); handleDeleteCustomer(customer.customerId); }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </button>

                {isOpen && (
                  <div className="admin-customer__body">
                    <div className="admin-detail-grid">
                      <div><span>User ID</span><p>{customer.userId || "—"}</p></div>
                      <div><span>Email ID</span><p>{customer.email || "—"}</p></div>
                      <div><span>Address</span><p>{customer.address || "—"}</p></div>
                      <div><span>Aadhaar</span><p>{customer.aadhaar || "—"}</p></div>
                      <div><span>PAN</span><p>{customer.pan || "—"}</p></div>
                      <div><span>Plot</span><p>{formatPlotNumber(customer.plotNumber, customer.plotPending)}</p></div>
                      <div><span>Size</span><p>{formatTentativeSize(customer) || "—"} @ {formatCurrency(customer.basicRatePerSqYd)}/yd</p></div>
                      <div><span>BSP</span><p>{formatCurrency(customer.bsp || customer.totalAmount)}</p></div>
                      <div><span>PLC</span><p>{formatPlcLabels(customer.plc, customer.noPlc, customer.plcPending)}</p></div>
                    </div>

                    <div className="admin-progress">
                      <div className="admin-progress__bar"><div style={{ width: `${paidPercent}%` }} /></div>
                      <span>{paidPercent}% collected · Updated {formatDate(customer.lastUpdatedAt)}</span>
                    </div>

                    <h4>Payment Received</h4>
                    {customer.payments?.length > 0 ? (
                      <div className="admin-table-wrap">
                        <table className="admin-table">
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Amount</th>
                              <th>Cheque / RTGS No.</th>
                              <th>Bank</th>
                              <th />
                            </tr>
                          </thead>
                          <tbody>
                            {customer.payments.map((p, idx) => {
                              const isEditingPayment =
                                editingPayment?.customerId === customer.customerId
                                && editingPayment?.index === idx;
                              const editDraft =
                                paymentEditDrafts[paymentEditKey(customer.customerId, idx)]
                                || EMPTY_PAYMENT_DRAFT;

                              if (isEditingPayment) {
                                return (
                                  <tr key={`${p.date}-${idx}-edit`} className="admin-table__edit-row">
                                    <td>
                                      <input
                                        type="date"
                                        value={editDraft.date}
                                        onChange={(e) => updatePaymentEditDraft(customer.customerId, idx, "date", e.target.value)}
                                        className="admin-table-input"
                                      />
                                    </td>
                                    <td>
                                      <input
                                        type="number"
                                        value={editDraft.amount}
                                        onChange={(e) => updatePaymentEditDraft(customer.customerId, idx, "amount", e.target.value)}
                                        className="admin-table-input admin-table-input--wide"
                                      />
                                    </td>
                                    <td>
                                      <input
                                        value={editDraft.chequeRtgs}
                                        onChange={(e) => updatePaymentEditDraft(customer.customerId, idx, "chequeRtgs", e.target.value)}
                                        className="admin-table-input"
                                      />
                                    </td>
                                    <td>
                                      <input
                                        value={editDraft.bank}
                                        onChange={(e) => updatePaymentEditDraft(customer.customerId, idx, "bank", e.target.value)}
                                        className="admin-table-input"
                                      />
                                    </td>
                                    <td className="admin-table__actions">
                                      <button
                                        type="button"
                                        className="admin-btn admin-btn--primary admin-btn--sm"
                                        onClick={() => savePaymentEdit(customer.customerId, idx)}
                                        disabled={loading}
                                      >
                                        Save
                                      </button>
                                      <button
                                        type="button"
                                        className="admin-btn admin-btn--ghost admin-btn--sm"
                                        onClick={cancelEditPayment}
                                        disabled={loading}
                                      >
                                        Cancel
                                      </button>
                                    </td>
                                  </tr>
                                );
                              }

                              return (
                                <tr key={`${p.date}-${idx}`}>
                                  <td>{formatDate(p.date)}</td>
                                  <td>{formatCurrency(p.amount)}</td>
                                  <td>{p.chequeRtgs || "—"}</td>
                                  <td>{p.bank || "—"}</td>
                                  <td className="admin-table__actions">
                                    <button
                                      type="button"
                                      className="admin-btn admin-btn--ghost admin-btn--sm"
                                      onClick={() => startEditPayment(customer.customerId, idx, p)}
                                      disabled={loading}
                                    >
                                      Edit
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="admin-muted">No payments recorded yet.</p>
                    )}

                    <h4>Record New Payment</h4>
                    <div className="admin-grid admin-grid--4 admin-payment-form">
                      <label className="admin-field">
                        <span>Date</span>
                        <input type="date" value={draft.date} onChange={(e) => setPaymentDrafts((prev) => ({ ...prev, [customer.customerId]: { ...draft, date: e.target.value } }))} />
                      </label>
                      <label className="admin-field">
                        <span>Amount</span>
                        <input type="number" value={draft.amount} onChange={(e) => setPaymentDrafts((prev) => ({ ...prev, [customer.customerId]: { ...draft, amount: e.target.value } }))} />
                      </label>
                      <label className="admin-field">
                        <span>Cheque / RTGS No.</span>
                        <input value={draft.chequeRtgs} onChange={(e) => setPaymentDrafts((prev) => ({ ...prev, [customer.customerId]: { ...draft, chequeRtgs: e.target.value } }))} />
                      </label>
                      <label className="admin-field">
                        <span>Bank</span>
                        <input value={draft.bank} onChange={(e) => setPaymentDrafts((prev) => ({ ...prev, [customer.customerId]: { ...draft, bank: e.target.value } }))} />
                      </label>
                    </div>
                    <div className="admin-payment-actions">
                      <button type="button" className="admin-btn admin-btn--primary" onClick={() => addPayment(customer.customerId)}>
                        Record Payment
                      </button>
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
